export type NavItem = {
  href: string;
  key:
    | "home"
    | "property"
    | "units"
    | "properties"
    | "prospects"
    | "tenants"
    | "leases"
    | "about"
    | "contact"
    | "overview"
    | "premises"
    | "documents"
    | "tickets"
    | "applications"
    | "meters"
    | "spotPrices"
    | "billing"
    | "rules"
    | "forms"
    | "events"
    | "protocols"
    | "sitePlan"
    | "content"
    | "settings"
    | "users"
    | "modules"
    | "areaTypes"
    | "fireSafety"
    | "fireOverview"
    | "fireEmergency"
    | "fireInstruction"
    | "fireStatus"
    | "fireDeviations"
    | "fireInspections"
    | "keys";
};

export type NavGroup = {
  key:
    | "crm"
    | "resources"
    | "operations"
    | "documentsGroup"
    | "site"
    | "settings"
    | "fireSafety";
  items: NavItem[];
};

export type NavModel = {
  overview?: NavItem;
  groups: NavGroup[];
  flat: NavItem[];
};

export const publicNav: NavModel = {
  groups: [],
  flat: [
    { href: "/", key: "home" },
    { href: "/property", key: "property" },
    { href: "/units", key: "units" },
    { href: "/tenants", key: "tenants" },
    { href: "/about", key: "about" },
    { href: "/contact", key: "contact" },
  ],
};

export const portalNav: NavModel = {
  overview: { href: "/portal", key: "overview" },
  flat: [{ href: "/portal/premises", key: "premises" }],
  groups: [
    {
      key: "fireSafety",
      items: [
        { href: "/portal/fire-safety", key: "fireEmergency" },
        { href: "/portal/fire-safety/instruction", key: "fireInstruction" },
        { href: "/portal/fire-safety/status", key: "fireStatus" },
      ],
    },
    {
      key: "operations",
      items: [
        { href: "/portal/tickets", key: "tickets" },
        { href: "/portal/events", key: "events" },
        { href: "/portal/meters", key: "meters" },
        { href: "/portal/prices", key: "spotPrices" },
        { href: "/portal/rules", key: "rules" },
        { href: "/portal/users", key: "users" },
      ],
    },
    {
      key: "documentsGroup",
      items: [
        { href: "/portal/documents", key: "documents" },
        { href: "/portal/site-plan", key: "sitePlan" },
        { href: "/portal/protocols", key: "protocols" },
        { href: "/portal/forms", key: "forms" },
      ],
    },
  ],
};

export const adminNav: NavModel = {
  overview: { href: "/admin", key: "overview" },
  groups: [
    {
      key: "crm",
      items: [
        { href: "/admin/prospects", key: "prospects" },
        { href: "/admin/tenants", key: "tenants" },
        { href: "/admin/tenants/leases", key: "leases" },
      ],
    },
    {
      key: "resources",
      items: [
        { href: "/admin/properties", key: "properties" },
        { href: "/admin/units", key: "units" },
        { href: "/admin/keys", key: "keys" },
      ],
    },
    {
      key: "fireSafety",
      items: [
        { href: "/admin/fire-safety", key: "fireOverview" },
        { href: "/admin/fire-safety/deviations", key: "fireDeviations" },
        { href: "/admin/fire-safety/inspections", key: "fireInspections" },
      ],
    },
    {
      key: "operations",
      items: [
        { href: "/admin/tickets", key: "tickets" },
        { href: "/admin/forms/submissions", key: "applications" },
        { href: "/admin/events", key: "events" },
        { href: "/admin/meters", key: "meters" },
        { href: "/admin/prices", key: "spotPrices" },
        { href: "/admin/billing", key: "billing" },
        { href: "/admin/rules", key: "rules" },
      ],
    },
    {
      key: "documentsGroup",
      items: [
        { href: "/admin/documents", key: "documents" },
        { href: "/admin/protocols", key: "protocols" },
        { href: "/admin/forms", key: "forms" },
      ],
    },
    {
      key: "settings",
      items: [
        { href: "/admin/settings", key: "settings" },
        { href: "/admin/settings/users", key: "users" },
        { href: "/admin/settings/modules", key: "modules" },
        { href: "/admin/units/area-types", key: "areaTypes" },
        { href: "/admin/content", key: "content" },
      ],
    },
  ],
  flat: [],
};

export function pathMatches(pathname: string, href: string) {
  if (href === "/") {
    return pathname === "/";
  }
  if (href === "/admin" || href === "/portal" || href === "/admin/settings") {
    return pathname === href;
  }
  if (href === "/admin/settings/users") {
    return pathname === href || pathname.startsWith(`${href}/`);
  }
  if (href === "/admin/tenants") {
    if (pathname === "/admin/tenants") return true;
    if (pathname.startsWith("/admin/tenants/leases")) return false;
    return pathname.startsWith("/admin/tenants/");
  }
  // Skjemaer templates vs Drift → Søknader (submissions share the /admin/forms prefix).
  if (href === "/admin/forms") {
    if (pathname === "/admin/forms") return true;
    if (pathname.startsWith("/admin/forms/submissions")) return false;
    return pathname.startsWith("/admin/forms/");
  }
  if (href === "/admin/fire-safety") {
    if (pathname === "/admin/fire-safety") return true;
    if (
      pathname.startsWith("/admin/fire-safety/deviations") ||
      pathname.startsWith("/admin/fire-safety/inspections")
    ) {
      return false;
    }
    return pathname.startsWith("/admin/fire-safety/");
  }
  if (href === "/portal/fire-safety") {
    return pathname === "/portal/fire-safety";
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}
